/**
 * Centralized Iconify icon mappings for DMSuite Dashboard.
 * Uses a cohesive Phosphor Duotone (ph:*-duotone) collection.
 *
 * All icons have been verified against the official Iconify API.
 */

export const DASHBOARD_ICONS = {
  // Key Metrics
  needsReview: 'ph:clipboard-text-duotone',
  inProgress: 'ph:clock-countdown-duotone',
  completed: 'ph:check-circle-duotone',
  openRequests: 'ph:ticket-duotone',
  leadsCaptured: 'ph:user-plus-duotone',
  totalTasks: 'ph:stack-duotone',

  // Services
  chatbot: 'ph:robot-duotone',
  voiceAssistant: 'ph:waveform-duotone',
  automation: 'ph:tree-structure-duotone',
  ads: 'ph:megaphone-duotone',
  videoProduction: 'ph:film-slate-duotone',
  seoGeo: 'ph:magnifying-glass-duotone',
  socialMedia: 'ph:chats-circle-duotone',
  promotionalMaterials: 'ph:palette-duotone',
  ticketSupport: 'ph:ticket-duotone',
  website: 'ph:desktop-duotone',
  gbp: 'ph:storefront-duotone',
  onboarding: 'ph:rocket-launch-duotone',
  defaultService: 'ph:sparkle-duotone',

  // Recent Updates & Deliveries (by event type)
  eventCreated: 'ph:plus-circle-duotone',
  eventUpdated: 'ph:arrows-clockwise-duotone',
  eventCompleted: 'ph:check-circle-duotone',
  eventReview: 'ph:clipboard-text-duotone',
  eventTicket: 'ph:ticket-duotone',
  eventReport: 'ph:file-text-duotone',
  eventMessage: 'ph:chats-circle-duotone',

  // Actions & Controls
  calendar: 'ph:calendar-blank-duotone',
  download: 'ph:download-simple-duotone',
  preview: 'ph:eye-duotone',
  search: 'ph:magnifying-glass-duotone',
  sparkle: 'ph:sparkle-duotone',
  warning: 'ph:warning-circle-duotone',
  refresh: 'ph:arrows-clockwise-duotone',
  plus: 'ph:plus-circle-duotone',
  check: 'ph:check-circle-duotone',
  layers: 'ph:stack-duotone',
  folder: 'ph:folder-open-duotone',
  close: 'ph:x-bold',
  externalLink: 'ph:arrow-square-out-duotone',
  chevronDown: 'ph:caret-down-bold',
  chevronUp: 'ph:caret-up-bold',
  arrowRight: 'ph:arrow-right-bold',
} as const;

/**
 * Standard sizing constants for dashboard icons
 */
export const DASHBOARD_ICON_SIZES = {
  card: 24,
  badge: 14,
  control: 16,
  preview: 20,
  emptyState: 32,
} as const;

/**
 * Maps a service name to its dedicated Phosphor Duotone Iconify icon.
 */
export function getServiceIconName(serviceName: string): string {
  const value = String(serviceName || '').toLowerCase();

  // 1. Chatbot vs Voice Assistant
  if (value.includes('voice assistant') || value.includes('voice') || value.includes('audio')) {
    return DASHBOARD_ICONS.voiceAssistant;
  }
  if (value.includes('chatbot') || value.includes('bot') || value.includes('ai chat')) {
    return DASHBOARD_ICONS.chatbot;
  }

  // 2. Video Production
  if (value.includes('video') || value.includes('film') || value.includes('clip') || value.includes('motion')) {
    return DASHBOARD_ICONS.videoProduction;
  }

  // 3. Marketing Automations
  if (value.includes('automation') || value.includes('workflow') || value.includes('journey')) {
    return DASHBOARD_ICONS.automation;
  }

  // 4. Ads Management
  if (value.includes('ads') || value.includes('advertising') || value.includes('campaign')) {
    return DASHBOARD_ICONS.ads;
  }

  // 5. Promotional Materials / Design
  if (value.includes('promotional') || value.includes('design') || value.includes('graphic') || value.includes('creative')) {
    return DASHBOARD_ICONS.promotionalMaterials;
  }

  // 6. SEO & GEO Management
  if (value.includes('seo') || value.includes('geo') || value.includes('search')) {
    return DASHBOARD_ICONS.seoGeo;
  }

  // 7. Social Media Management
  if (value.includes('social media') || value.includes('social') || value.includes('instagram') || value.includes('facebook')) {
    return DASHBOARD_ICONS.socialMedia;
  }

  // 8. Website & Maintenance
  if (value.includes('website') || value.includes('web') || value.includes('landing')) {
    return DASHBOARD_ICONS.website;
  }

  // 9. Google Business Profile (GBP)
  if (value.includes('gbp') || value.includes('google business') || value.includes('store') || value.includes('local')) {
    return DASHBOARD_ICONS.gbp;
  }

  // 10. Onboarding
  if (value.includes('onboarding') || value.includes('setup') || value.includes('kickoff')) {
    return DASHBOARD_ICONS.onboarding;
  }

  // 11. Add-on & Ticket Support
  if (value.includes('add-on') || value.includes('addon') || value.includes('ticket') || value.includes('support')) {
    return DASHBOARD_ICONS.ticketSupport;
  }

  return DASHBOARD_ICONS.defaultService;
}

/**
 * Maps an activity/event type and title to its dedicated Phosphor Duotone Iconify icon.
 */
export function getActivityIconName(type: string, title = ''): string {
  const key = String(type || '').toLowerCase();
  const text = String(title || '').toLowerCase();

  // Completed events
  if (
    key.includes('completed') ||
    key.includes('done') ||
    key.includes('closed') ||
    key.includes('approved') ||
    key.includes('published') ||
    text.includes('completed') ||
    text.includes('approved')
  ) {
    return DASHBOARD_ICONS.eventCompleted;
  }

  // Needs review / feedback events
  if (
    key.includes('task_marked_sent') ||
    key.includes('review') ||
    key.includes('feedback') ||
    key.includes('approval') ||
    text.includes('review') ||
    text.includes('approval') ||
    text.includes('feedback')
  ) {
    return DASHBOARD_ICONS.eventReview;
  }

  // Report events
  if (key.includes('report') || text.includes('report')) {
    return DASHBOARD_ICONS.eventReport;
  }

  // Ticket / Support events
  if (key.includes('ticket') || key.includes('request') || text.includes('ticket')) {
    return DASHBOARD_ICONS.eventTicket;
  }

  // Message / Chat events
  if (key.includes('message') || key.includes('chat') || key.includes('comment') || key.includes('note')) {
    return DASHBOARD_ICONS.eventMessage;
  }

  // Created events
  if (key.includes('task_created') || key.includes('created') || key.includes('added') || key.includes('new')) {
    return DASHBOARD_ICONS.eventCreated;
  }

  // Updated / Progress events
  if (key.includes('task_updated') || key.includes('updated') || key.includes('modified') || key.includes('progress')) {
    return DASHBOARD_ICONS.eventUpdated;
  }

  return DASHBOARD_ICONS.inProgress;
}
